import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// Import WebP versions at different sizes for responsive loading
import heroImageSmall from "@/assets/hero-mansion-interior.jpg?w=640&format=webp&quality=80";
import heroImageMedium from "@/assets/hero-mansion-interior.jpg?w=1024&format=webp&quality=80";
import heroImageLarge from "@/assets/hero-mansion-interior.jpg?w=1920&format=webp&quality=80";
import heroImageFallback from "@/assets/hero-mansion-interior.jpg";

const Hero = () => {
  return (
    <section id="home" className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
      {/* Background Image with WebP and responsive srcset */}
      <div className="absolute inset-0">
        <picture>
          <source
            type="image/webp"
            srcSet={`${heroImageSmall} 640w, ${heroImageMedium} 1024w, ${heroImageLarge} 1920w`}
            sizes="100vw"
          />
          <img
            src={heroImageFallback}
            alt="Modern luxury home interior with vaulted ceilings"
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1920}
            height={1080}
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal/85 via-charcoal/65 to-charcoal/75" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 text-center pt-20 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="inline-block text-gold text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-4 sm:mb-6 font-medium"
          >
            Chicago's Premier Real Estate Expert
          </motion.span>

          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-display font-semibold text-white leading-tight mb-4 sm:mb-6">
            Find Your Dream Home
            <br />
            <span className="text-gold">in Chicago</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8 sm:mb-10 font-light leading-relaxed px-2">
            With over 20 years of experience and $900M+ in sales, Brant Booker
            delivers unparalleled expertise in Chicago's luxury real estate market.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <Button asChild variant="heroSolid" size="lg" className="w-full sm:w-auto text-base sm:text-lg h-12 sm:h-14 px-6 sm:px-10">
              <Link to="/properties">
                View Properties
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Link>
            </Button>
            <Button asChild variant="hero" size="lg" className="w-full sm:w-auto text-base sm:text-lg h-12 sm:h-14 px-6 sm:px-10">
              <Link to="/contact">
                Contact Brant
              </Link>
            </Button>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="grid grid-cols-3 gap-4 sm:gap-8 mt-10 sm:mt-16 pt-8 sm:pt-10 border-t border-white/20 max-w-2xl mx-auto"
          >
            <div>
              <div className="text-xl sm:text-3xl md:text-4xl font-display font-semibold text-white">$900M+</div>
              <div className="text-xs sm:text-sm text-white/70 mt-1">Total Sales</div>
            </div>
            <div>
              <div className="text-xl sm:text-3xl md:text-4xl font-display font-semibold text-white">20+</div>
              <div className="text-xs sm:text-sm text-white/70 mt-1">Years Experience</div>
            </div>
            <div>
              <div className="text-xl sm:text-3xl md:text-4xl font-display font-semibold text-white">1,900+</div>
              <div className="text-xs sm:text-sm text-white/70 mt-1">Transactions</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
